import React, { useState, useEffect, useRef } from 'react';
import { useStoriesStore } from '../../stores/useDataStore'; // 🟢 2. Importamos el store de historias
import { useAuthStore } from '../../stores/useAuthStore';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { getMediaUrl } from '../../utils/imageUtils';

const StoryViewer = ({ isOpen, onClose, modalData }) => {
  const { stories = [], initialStoryId } = modalData || {};
  const likeStory = useStoriesStore((state) => state.likeStory);
  const addCommentToStory = useStoriesStore((state) => state.addCommentToStory);
  const currentUser = useAuthStore((state) => state.currentUser);

  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [commentText, setCommentText] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);

  // Encuentra la historia inicial cuando el modal se abre
  useEffect(() => {
    if (isOpen && initialStoryId) {
      const storyIdx = stories.findIndex(s => s._id === initialStoryId);
      if (storyIdx !== -1) setCurrentStoryIndex(storyIdx);
    }
  }, [isOpen, initialStoryId, stories]);

  const currentStory = stories[currentStoryIndex];
  const currentScene = currentStory?.scenes?.[currentSceneIndex];
  const storyAuthorIsSenator = currentStory?.author?.rank === 'senator' || currentStory?.author?.tier === 'senador';

  // Maneja el avance automático de las escenas con duración dinámica para anuncios
  useEffect(() => {
    if (!isOpen || !currentScene) return;

    setProgress(0);
    clearInterval(timerRef.current);

    // Si la historia es un anuncio soberano y tiene storyDuration, úsalo (en segundos)
    let sceneDuration = currentScene.duration || 5000;
    // Compatibilidad: storyDuration puede venir de story o de placement/zone
    const storyDurationValue = currentStory?.storyDuration || currentStory?.zone?.storyDuration || currentStory?.placement?.storyDuration;
    if (currentStory?.isSovereign && storyDurationValue) {
      sceneDuration = storyDurationValue * 1000; // storyDuration en segundos
    }
    const intervalTime = 50;
    let elapsedTime = 0;

    timerRef.current = setInterval(() => {
      elapsedTime += intervalTime;
      setProgress((elapsedTime / sceneDuration) * 100);
      if (elapsedTime >= sceneDuration) goToNextScene();
    }, intervalTime);

    return () => clearInterval(timerRef.current);
  }, [currentScene, isOpen, currentStory]);

  // --- Funciones de Navegación ---
  const goToNextScene = () => {
    if (currentSceneIndex < currentStory.scenes.length - 1) setCurrentSceneIndex(prev => prev + 1);
    else goToNextStory();
  };

  const goToPrevScene = () => {
    if (currentSceneIndex > 0) setCurrentSceneIndex(prev => prev - 1);
    else goToPrevStory();
  };

  const goToNextStory = () => {
    if (currentStoryIndex < stories.length - 1) {
      setCurrentStoryIndex(prev => prev + 1);
      setCurrentSceneIndex(0);
    } else onClose?.();
  };

  const goToPrevStory = () => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(prev => prev - 1);
      setCurrentSceneIndex(0);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    const result = await addCommentToStory(currentStory._id, commentText);
    if (result.success) {
      setCommentText('');
    }
  };

  if (!isOpen || !stories || stories.length === 0) return null;
  if (!currentStory || !currentStory.scenes || currentStory.scenes.length === 0) return null;

  const sceneTimestamp = currentScene?.createdAt ? new Date(currentScene.createdAt) : null;
  const sceneTimeLabel = sceneTimestamp && !Number.isNaN(sceneTimestamp.getTime())
    ? formatDistanceToNow(sceneTimestamp, { locale: es, addSuffix: true })
    : 'hace un momento';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center animate-fade-in"
      style={{ zIndex: 9999 }}
    >
      <div className="relative w-full max-w-sm h-[90vh] bg-gray-800 rounded-lg overflow-hidden shadow-2xl">
        <div className="absolute top-2 left-2 right-2 flex space-x-1 z-20">
          {currentStory.scenes.map((_, index) => {
            // Duración dinámica para la barra de progreso
            let barDuration = currentStory?.isSovereign && currentStory?.storyDuration
              ? currentStory.storyDuration * 1000
              : currentStory.scenes[index]?.duration || 5000;
            return (
              <div key={index} className="flex-1 h-1 bg-white bg-opacity-30 rounded-full">
                <div
                  className="h-full bg-white rounded-full"
                  style={{
                    width: `${
                      index < currentSceneIndex
                        ? 100
                        : index === currentSceneIndex
                        ? progress
                        : 0
                    }%`,
                    transition: index === currentSceneIndex ? `width ${barDuration}ms linear` : undefined,
                  }}
                />
              </div>
            );
          })}
        </div>
        <img src={getMediaUrl(currentScene.mediaUrl)} alt="Story scene" className="w-full h-full object-cover" />
        <div className="absolute top-0 left-0 right-0 p-4 flex items-center space-x-3 z-20">
          <img
            src={currentStory.author?.avatar ? getMediaUrl(currentStory.author.avatar) : '/images/avatar/DefaultProfile.jpg'}
            alt={currentStory.author.username}
            className={`w-10 h-10 rounded-full border-2 border-white ${storyAuthorIsSenator ? 'gold-aura' : ''}`}
          />
          <div>
            <p className="font-bold text-white">{currentStory.author.username}</p>
            <p className="text-xs text-gray-300">{sceneTimeLabel}</p>
          </div>
        </div>
        <div className="absolute inset-0 flex justify-between items-center z-10">
          <button onClick={goToPrevScene} className="w-1/2 h-full"></button>
          <button onClick={goToNextScene} className="w-1/2 h-full"></button>
        </div>
        <button onClick={onClose} className="absolute top-4 right-4 text-white text-2xl z-30">
          <i className="fas fa-times"></i>
        </button>

        {/* 🟢 SOLUCIÓN: Interfaz de Comentarios */}
        <div className="absolute bottom-0 left-0 right-0 p-4 z-20">
          {showComments && (
            <div className="bg-black bg-opacity-50 p-3 rounded-lg mb-2 max-h-48 overflow-y-auto">
              {currentStory.comments && currentStory.comments.length > 0 ? (
                currentStory.comments.map(comment => (
                  <div key={comment._id} className="text-white text-sm mb-1 flex items-start">
                    <img
                      src={comment.user?.avatar ? getMediaUrl(comment.user.avatar) : '/images/avatar/DefaultProfile.jpg'}
                      alt={comment.user?.username}
                      className={`w-5 h-5 rounded-full mr-2 ${comment.user?.rank === 'senator' || comment.user?.tier === 'senador' ? 'gold-aura' : ''}`}
                    />
                    <div>
                      <span className="font-bold">{comment.user.username}</span>
                      <span className="ml-2">{comment.text}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-400 text-sm text-center">No hay comentarios todavía.</p>
              )}
            </div>
          )}
          <div className="flex items-center space-x-2">
            <button onClick={() => setShowComments(prev => !prev)} className="text-white text-xl">
              <i className="far fa-comment"></i>
            </button>
            <form onSubmit={handleCommentSubmit} className="flex-grow">
              <input
                type="text"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Escribe un comentario..."
                className="w-full bg-black bg-opacity-50 text-white placeholder-gray-400 border-none rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={(e) => e.stopPropagation()} // Evita que el clic cierre el modal
              />
            </form>
            <button onClick={() => likeStory(currentStory._id)} className="text-white text-xl">
              <i className={`fa-heart ${currentStory.likes?.includes(currentUser._id) ? 'fas text-red-500' : 'far'}`}></i>
            </button>
          </div>
        </div>
      </div>
        </div>
  );
};

export default StoryViewer;
